import {
  ActionIcon,
  Center,
  Input,
  Paper,
  ScrollArea,
  SimpleGrid,
  Skeleton,
  Stack,
  Text
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconTrash } from '@tabler/icons-react'
import { ReactNode, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { AddButtonCard } from '@/components/ui-shared/AddButtonCard/AddButtonCard'
import { SelectableCard } from '@/components/ui-shared/SelectableCard/SelectableCard'
import { MilestonePipelineIcon } from '@/components/vlm/MilestonePipelineIcon'
import { useGetMilestoneServerList } from '@/queries/vlmQueries'
import { MilestoneServer } from '@/types/vlm'
import { MAX_ITEMS_WITHOUT_PAGINATION } from '@/utils/pagination'
import { MilestoneServerCreateModal } from './MilestoneServerCreateModal'
import { MilestoneServerDeleteModal } from './MilestoneServerDeleteModal'
import { MilestoneServerForm } from './MilestoneServerForm'

type MilestoneServerPickerProps = {
  value: string
  onChange: (serverId: string) => void
  label: ReactNode
  description?: ReactNode
  error?: ReactNode
  // Device used to test the connection of a newly created server.
  deviceId: string
  skeletonCount?: number
}

export const MilestoneServerPicker = ({
  value,
  onChange,
  label,
  description,
  error,
  deviceId,
  skeletonCount = 4
}: MilestoneServerPickerProps) => {
  const intl = useIntl()
  const [serverToDelete, setServerToDelete] = useState<MilestoneServer | null>(
    null
  )
  const [createOpened, { open: openCreate, close: closeCreate }] =
    useDisclosure(false)

  const { data, isLoading, isError } = useGetMilestoneServerList({
    limit: MAX_ITEMS_WITHOUT_PAGINATION
  })

  const servers = data?.results ?? []

  const handleServerDeleted = () => {
    if (value === serverToDelete?.id) onChange('')
  }

  const renderServerList = () => {
    if (isLoading)
      return (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} p="sm">
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <Skeleton key={i} height={140} radius="md" />
          ))}
        </SimpleGrid>
      )

    if (isError)
      return (
        <Center p="xl">
          <Text size="sm" c="red">
            <FormattedMessage id="general.list.error" />
          </Text>
        </Center>
      )

    return (
      <ScrollArea.Autosize mah={320}>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} p="sm">
          <AddButtonCard
            height={140}
            label={<FormattedMessage id="vlm.milestoneServers.create.add" />}
            compact
            onClick={openCreate}
          />

          {servers.map((server) => (
            <SelectableCard
              key={server.id}
              height={140}
              renderAs="div"
              isSelected={value === server.id}
              onClick={() => onChange(server.id)}
            >
              <ActionIcon
                variant="subtle"
                color="red"
                pos="absolute"
                top={4}
                right={4}
                aria-label={intl.formatMessage({
                  id: 'vlm.pipelines.create.actions.deleteButton'
                })}
                onClick={(e) => {
                  e.stopPropagation()
                  setServerToDelete(server)
                }}
              >
                <IconTrash size={16} />
              </ActionIcon>

              <Stack gap={4} align="center" justify="center" h="100%">
                <MilestonePipelineIcon size={24} />

                <Text size="sm" fw={600} ta="center" lineClamp={2}>
                  {server.name}
                </Text>

                <Text
                  size="xs"
                  c="dimmed"
                  ta="center"
                  lineClamp={2}
                  style={{ wordBreak: 'break-all' }}
                >
                  {server.server_url}
                </Text>
              </Stack>
            </SelectableCard>
          ))}
        </SimpleGrid>
      </ScrollArea.Autosize>
    )
  }

  return (
    <>
      <Input.Wrapper label={label} description={description} error={error}>
        <Paper my={4} withBorder>
          {renderServerList()}
        </Paper>
      </Input.Wrapper>

      <MilestoneServerCreateModal opened={createOpened} onClose={closeCreate}>
        <MilestoneServerForm
          deviceId={deviceId}
          onCreated={(server) => onChange(server.id)}
          onClose={closeCreate}
        />
      </MilestoneServerCreateModal>

      <MilestoneServerDeleteModal
        opened={serverToDelete !== null}
        server={serverToDelete}
        onDeleted={handleServerDeleted}
        onClose={() => setServerToDelete(null)}
      />
    </>
  )
}
